import React from 'react'
import { createClasses } from '../helper'
import styles from './BrokerDashboardBox.module.scss'

const InfoIcon = () => (
  <svg
    className={styles.infoIcon}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <circle cx="8" cy="8" r="6.5" stroke="#617D99" strokeWidth="1.2" />
    <rect x="7.35" y="6.8" width="1.3" height="4.4" rx="0.65" fill="#617D99" />
    <circle cx="8" cy="4.9" r="0.85" fill="#617D99" />
  </svg>
)

// Default trailing header icon (Figma "bird" placeholder — pass `icon` to override)
const BirdIcon = () => (
  <svg
    className={styles.icon}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#617D99"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 7h.01M3.4 18H12a8 8 0 0 0 8-8V7a4 4 0 0 0-7.28-2.3L2 20" />
    <path d="m20 7 2 .5-2 .5" />
    <path d="M10 18v3" />
    <path d="M14 17.75V21" />
    <path d="M7 18a6 6 0 0 0 3.84-10.61" />
  </svg>
)

const badgeVariants = {
  wex: { className: styles['badge-wex'], label: 'WEX' },
  marqeta: { className: styles['badge-marqeta'], label: 'MARQETA' },
} as const

const StatBadge = ({ variant }: { variant: keyof typeof badgeVariants }) => {
  const b = badgeVariants[variant]
  return (
    <div className={createClasses(styles.badge, b.className)}>
      <p className={styles.badgeLabel}>{b.label}</p>
    </div>
  )
}

export type BrokerDashboardBoxProps = {
  /** Stat title shown in the header row */
  title?: string
  /** Helper text under the number(s) */
  description?: string
  /** Primary stat value — a string, or a node (e.g. a loading spinner) in its place */
  prop1stNumber?: React.ReactNode
  /** Secondary stat value, shown only when qty="2" */
  prop2ndNumber?: string
  /** "1" = single stat, "2" = two stats (WEX / Marqeta badges) separated by a divider */
  qty?: '1' | '2'
  /** "hovered" swaps to the muted surface/border; "focused" swaps the border to the teal focus color */
  state?: 'enabled' | 'hovered' | 'focused'
  /** Show the info tooltip icon next to the title */
  showInfo?: boolean
  /** Show the trailing 24x24 header icon (defaults to the placeholder bird icon) */
  showIcon?: boolean
  /** Show the description line under the number(s) */
  showDescription?: boolean
  /** Custom trailing header icon; overrides the default placeholder when provided */
  icon?: React.ReactNode
  className?: string
}

export const BrokerDashboardBox = ({
  className,
  title = 'Title',
  description = 'Description Text',
  prop1stNumber = '6372',
  prop2ndNumber = '6372',
  qty = '1',
  state = 'enabled',
  showInfo = true,
  showIcon = true,
  showDescription = true,
  icon,
}: BrokerDashboardBoxProps) => {
  const isDouble = qty === '2'

  return (
    <div className={createClasses(styles.box, styles[state], className)}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <p className={styles.title}>{title}</p>
          {showInfo && <InfoIcon />}
        </div>
        {showIcon && (icon ?? <BirdIcon />)}
      </div>

      <div className={styles.stats}>
        {!isDouble && (
          <>
            <div className={styles.number}>{prop1stNumber}</div>
            {showDescription && (
              <p className={styles.description}>{description}</p>
            )}
          </>
        )}

        {isDouble && (
          <>
            <div className={styles.doubleRow}>
              <div className={styles.statGroup}>
                <StatBadge variant="wex" />
                <div className={styles.number}>{prop1stNumber}</div>
              </div>
              <div className={styles.divider} />
              <div className={styles.statGroup}>
                <StatBadge variant="marqeta" />
                <div className={styles.number}>{prop2ndNumber}</div>
              </div>
            </div>
            {showDescription && (
              <p className={styles.description}>{description}</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default BrokerDashboardBox
